import { useCallback, useRef } from "react";
import type { GridReadyEvent, RowClickedEvent } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { globalDefaultColDef } from "@/setup/ag-grid/mesf-theme";
import type { ProductSize } from "../models/ProductSize";
import { useArrowRowNavigation } from "@/controls/ag-grid/useArrowRowNavigation";
import { useTableData } from "../hooks/useTableData";

interface ProductsTableProps {
	products: ProductSize[];
	selectedProduct: ProductSize | null;
	onRowSelected: (product: ProductSize | null) => void;
	onDelete: (product: ProductSize) => void;
	onToggleActive: (product: ProductSize, active: boolean) => void;
}

export const ProductsTable = ({
	products,
	selectedProduct,
	onRowSelected,
	onDelete,
	onToggleActive,
}: ProductsTableProps) => {
	const gridRef = useRef<AgGridReact<ProductSize>>(null);
	const { columnDefs } = useTableData({ onDelete, onToggleActive });

	const onGridReady = useCallback(
		(_params: GridReadyEvent<ProductSize>) => {},
		[],
	);

	const onRowClicked = useCallback(
		(event: RowClickedEvent<ProductSize>) => {
			if (event.data) {
				onRowSelected(event.data);
			}
		},
		[onRowSelected],
	);

	const onCellKeyDown = useArrowRowNavigation<ProductSize>(onRowSelected);

	const getRowId = useCallback((params: { data: ProductSize }) => {
		return params.data.Id?.toString() ?? "";
	}, []);

	return (
		<AgGridReact<ProductSize>
			ref={gridRef}
			rowData={products}
			columnDefs={columnDefs}
			defaultColDef={globalDefaultColDef}
			onGridReady={onGridReady}
			onRowClicked={onRowClicked}
			onCellKeyDown={onCellKeyDown}
			getRowId={getRowId}
			rowSelection="single"
			suppressRowClickSelection={false}
			suppressContextMenu
			alwaysShowVerticalScroll
			suppressColumnVirtualisation
			animateRows={false}
			rowHeight={28}
			headerHeight={32}
			getRowStyle={(params) => {
				if (selectedProduct?.Id === params.data?.Id) {
					return { backgroundColor: "#e3f2fd", color: "inherit" };
				}
				if (params.data && !params.data.Active) {
					return { backgroundColor: "#f5f5f5", color: "#9e9e9e" };
				}
				return undefined;
			}}
		/>
	);
};
